<script setup>
import {useTagsStore, useUsersStore} from '@/stores'
import { computed, ref, onMounted, watch } from 'vue'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'

const tagsStore = useTagsStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const tagsPerPage = 5

const selectedTag = ref(null)

const searchValue = ref("")

const showingAll = ref(false)
const showingNew = ref(false)

const filtering = ref(false)
const currentPage = ref(1)
const orderBy = ref("createdAt")
const orderDirection = ref("-") // this should be "" or "-" for desc
const statusFilter = ref("all")

const isConfirmDialogOpen = ref(false)
const isShowingEditDialog = ref(false)
const isShowingNewDialog = ref(false)
const isActiveMembersDialogOpen = ref(false)
const selectedTagForMembers = ref(null)
const tagActiveMembersCounts = ref({})

const exportFilteredTags = function() {
  const items = filteredTags.value

  let itemWithMostKeys = {}
  for(let item of items){
    if(Object.keys(item).length > Object.keys(itemWithMostKeys).length){
      itemWithMostKeys = item
    }
  }

  const replacer = (key, value) => {
    //nulls, arrays, objects all export as empty string
    if(value === null || typeof value === "array" || typeof value === "object"){
      return ''
    }
    
    return value
  }

  const header = Object.keys(itemWithMostKeys)
  const csv = [
    header.join(','), // header row first
    ...items.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
  ].join('\r\n')

  var download = function(content, fileName, mimeType) {
    var a = document.createElement('a')
    mimeType = mimeType || 'application/octet-stream'

    if (navigator.msSaveBlob) { // IE10
      navigator.msSaveBlob(new Blob([content], {
        type: mimeType
      }), fileName)
    } else if (URL && 'download' in a) { //html5 A[download]
      a.href = URL.createObjectURL(new Blob([content], {
        type: mimeType
      }))
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      location.href = 'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
    }
  }

  download(csv, "tags.csv", 'text/csv;encoding:utf-8')

}

const dynamicSort = function(property) {
  var sortOrder = 1
  if(property[0] === "-") {
    sortOrder = -1
    property = property.substr(1)
  }
  
  return function (a,b) {
    let x = a[property] ? a[property] : ""
    let y = b[property] ? b[property] : ""

    var result = (x < y) ? -1 : (x > y) ? 1 : 0
    
    return result * sortOrder
  }
}

const showNew = async () => {
  showingNew.value = true
  showingAll.value = false
  searchValue.value = ""
  await tagsStore.getTags()
}

const pendingCount = computed(() => {
  return (tagsStore.tags || []).filter(t => t.status === 'pending').length
})

const filteredTags = computed(() => {
  filtering.value = true
  var returnArray = tagsStore.tags.filter(item => true) || []

  // Status filter
  if (statusFilter.value !== 'all') {
    returnArray = returnArray.filter(item => {
      return (item.status || 'approved') === statusFilter.value
    })
  }

  // Process search input
  if (searchValue.value != '' && searchValue.value && returnArray) {
    returnArray = returnArray.filter(item => {
      return item?.tagName?.toUpperCase()?.includes(searchValue.value.toUpperCase())
    })
  }

  returnArray.sort(dynamicSort(orderDirection.value+orderBy.value))


  setTimeout(() => {
    filtering.value = false
  }, 100)

  return returnArray
})

const showNewTagDialog = () => {
  isShowingNewDialog.value = true
}

const showEditTagDialog = async tagId => {
  await tagsStore.getTagById(tagId) 
  isShowingEditDialog.value = true
}

const showDeleteTagDialog = async tagId => {
  await tagsStore.getTagById(tagId)
  isConfirmDialogOpen.value = true 
}

const deleteTag = async tagId => {
  await tagsStore.deleteTag(tagId)
  await showNew()
}

const cloneTag = async tag => {
  const clonedData = {
    tagName: `${tag.tagName} (Copy)`,
    tagDescription: tag.tagDescription || '',
    tagShortDescription: tag.tagShortDescription || ''
  }
  const newTag = await tagsStore.createTag(clonedData)
  await showNew()
  if (newTag?._id) {
    await showEditTagDialog(newTag._id)
  }
}

const approveTag = async tag => {
  await tagsStore.updateTag(tag._id, { ...tag, status: 'approved' })
  await showNew()
}

const showActiveMembersDialog = async tag => {
  selectedTagForMembers.value = tag
  isActiveMembersDialogOpen.value = true
}

const loadAllCounts = async () => {
  try {
    const counts = await tagsStore.getBulkMemberCounts()
    tagActiveMembersCounts.value = counts
  } catch (error) {
    console.error('Error loading bulk counts:', error)
    tagActiveMembersCounts.value = {}
  }
}

onMounted(async () => {
  await showNew()
  await loadAllCounts()
})

watch(() => tagsStore.tags, async newTags => {
  if (newTags && newTags.length > 0) {
    await loadAllCounts()
  }
}, { deep: true })
</script>

<template>
  <div>
    <AdminEditTagDialog
      v-model:is-dialog-visible="isShowingEditDialog"
      :selected-tag="tagsStore.tag"
    />
    <AdminNewTagDialog v-model:is-dialog-visible="isShowingNewDialog" />
    <TagActiveMembersDialog
      v-model:is-dialog-visible="isActiveMembersDialogOpen"
      :tag-id="selectedTagForMembers?._id"
      :tag-name="selectedTagForMembers?.tagName"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      :confirmation-msg="`Are you sure you want to delete <span class='text-decoration-underline'>${tagsStore.tag.tagName}</span>? This is not reversible!`"
      @confirm="deleteTag(tagsStore.tag._id)"
    />

    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div class="account-card-content">
              <div class="mb-2">
                Sorting
              </div>
              <VSelect
                v-model="orderBy"
                class="mb-2"
                label="Order By"
                item-title="text"
                item-value="value"
                :items="[{text:'Created At', value: 'createdAt'}]"
                required
              />
              <VRadioGroup
                v-model="orderDirection"
                row
              >
                <VRadio
                  label="▲ Ascending"
                  value=""
                  density="compact"
                />
                <VRadio
                  label="▼ Descending"
                  value="-"
                  density="compact"
                />
              </VRadioGroup>

              <VSelect
                v-model="statusFilter"
                class="mb-4"
                label="Status"
                item-title="text"
                item-value="value"
                :items="[{text:'All', value: 'all'},{text:'Approved', value: 'approved'},{text:'Pending', value: 'pending'}]"
              />

              <VBtn
                class="account-card-primary-action-button"
                @click="showNewTagDialog"
              >
                <VIcon

                  start
                  size="18"
                  icon="mdi-add-circle-outline"
                />
                <span>New Tag</span>
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>

      <div>
        <VCard class="pa-10 pt-8">
          <VRow>
            <h3 class="user-dashboard-card-title mb-1">
              Tags ({{ tagsStore.tags.length }})
              <VChip
                v-if="pendingCount > 0"
                color="warning"
                size="x-small"
                class="ml-2"
                variant="tonal"
              >
                {{ pendingCount }} pending
              </VChip>
            </h3>
            <VSpacer />
            <VBtn
              color="grey-lighten-1"
              icon="mdi-file-export"
              variant="text"
              @click="exportFilteredTags"
            />
          </VRow>
          <VTextField
            v-model="searchValue"
            :loading="filtering"
            density="compact"
            variant="solo"
            :label="(filtering ? 'Syncing... ' : 'Search by tag name etc.')"
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            clearable
            @keyup="currentPage = 1"
          />
          <div>
            <template v-for="(tag, index) in filteredTags">
              <VCard
                v-if="((index >= (currentPage*tagsPerPage)-tagsPerPage) && (index <= currentPage*tagsPerPage))"
                :key="tag._id"
                class="mb-4"
                @click="async () => await showEditTagDialog(tag._id)"
              >
                <VCardTitle>
                  <b>{{ tag.tagName }}</b>
                  <VChip
                    :color="(tag.status || 'approved') === 'approved' ? 'success' : 'warning'"
                    size="x-small"
                    class="ml-2"
                    variant="tonal"
                  >
                    {{ tag.status || 'approved' }}
                  </VChip>
                </VCardTitle>
                   
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      density="compact"
                      class="admin-member-menu-button"
                      icon="mdi-dots-vertical"
                      v-bind="props"
                    />
                  </template>

                  <VList>
                    <VListItem
                      v-for="(item, i) in [
                        { title: 'Edit', action: async () => {
                          await showEditTagDialog(tag._id)
                        }},
                        ...((tag.status === 'pending') ? [{ title: 'Approve', action: async () => {
                          await approveTag(tag)
                        }}] : []),
                        { title: 'Clone', action: async () => {
                          await cloneTag(tag)
                        }},
                        { title: 'Delete', action: async () => {
                          await showDeleteTagDialog(tag._id)
                        }}
                      ]"
                      :key="i"
                      @click="item.action();"
                    >
                      <VListItemTitle>{{ item.title }}</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>

                <VCardItem class="py-0">
                  <p class="text-overline text-decoration-underline">
                    Long Description
                  </p>
                  <VCardText
                    v-if="tag.tagDescription"
                    class="pt-0 text-caption text-wrap"
                    v-html="sanitizeHtml(tag.tagDescription)"
                  />
                </VCardItem>

                <VCardItem class="py-0">
                  <p class="text-overline text-decoration-underline">
                    Short Description
                  </p>
                  <VCardText
                    v-if="tag.tagShortDescription"
                    class="pt-0 text-caption text-wrap"
                    v-html="sanitizeHtml(tag.tagShortDescription)"
                  />
                </VCardItem>

                <VCardItem class="py-0">
                  <VCardText class="pt-0">
                    <div 
                      style="cursor: pointer; color: #4DB6AC; text-decoration: underline;"
                      @click.stop="showActiveMembersDialog(tag)"
                    >
                      Total members: {{ tagActiveMembersCounts[tag._id] || 0 }}
                    </div>
                  </VCardText>
                </VCardItem>
              </VCard>
            </template>

            <VPagination
              v-model="currentPage"
              :length="Math.ceil(filteredTags.length / tagsPerPage)"
            />
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Write your own CSS for pagination */
.pagination .item {
  margin: 10px;
}

.item.active {
  color: red;
}

.admin-member-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: 20px;
}

.verified-icon {
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.verified-icon-pending {
  cursor: pointer;
  color: #ffd900;
  width: 1.25rem;
  height: 1.25rem;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only: this is the tag AUTHORING console (create/edit/delete
  # tag descriptions rendered elsewhere via v-html). The API enforces
  # requireAdmin() on every tag write; requiresAdmin gates the route so
  # non-admins can't reach the authoring surface at all (H10).
  requiresAdmin: true
</route>
